import { detectPlatform, formatForDisplay } from "@tanstack/hotkeys";

export type ShortcutPlatform = "mac" | "windows" | "linux";

/**
 * Single source of truth for application shortcuts. The same canonical hotkey
 * drives both the registered handler and the displayed hint.
 */
export const SHORTCUTS = {
  commandPalette: "Mod+K",
  toggleSidebar: "Mod+Shift+S",
  toggleLayout: "Mod+Shift+U",
  toggleFullscreen: "Mod+Shift+F",
  toggleTheme: "Mod+Shift+L",
  localeEnglish: "Mod+Shift+E",
  localePortuguese: "Mod+Shift+P",
  focusTableSearch: "Mod+J",
  submit: "Mod+Enter",
  stop: "Escape",
} as const;

export type ShortcutId = keyof typeof SHORTCUTS;

export function detectShortcutPlatform(): ShortcutPlatform {
  return detectPlatform();
}

const modifierAliases: Record<string, string> = {
  meta: "Mod",
  cmd: "Mod",
  command: "Mod",
  mod: "Mod",
  commandorcontrol: "Mod",
  ctrl: "Control",
  control: "Control",
  shift: "Shift",
  alt: "Alt",
  option: "Alt",
};

const keyAliases: Record<string, string> = {
  esc: "Escape",
  escape: "Escape",
  return: "Enter",
  enter: "Enter",
  space: "Space",
  spacebar: "Space",
  backspace: "Backspace",
  delete: "Delete",
  tab: "Tab",
  arrowup: "ArrowUp",
  arrowdown: "ArrowDown",
  arrowleft: "ArrowLeft",
  arrowright: "ArrowRight",
  up: "ArrowUp",
  down: "ArrowDown",
  left: "ArrowLeft",
  right: "ArrowRight",
  pageup: "PageUp",
  pagedown: "PageDown",
  home: "Home",
  end: "End",
};

/**
 * Normalizes legacy key arrays (`["meta", "shift", "l"]`) and loose key names
 * (`esc`, `enter`) into the canonical TanStack hotkey syntax (`Mod+Shift+L`).
 */
export function normalizeShortcutKeys(keys: string | string[]): string {
  const list = Array.isArray(keys) ? keys : [keys];

  return list
    .map((raw) => {
      const lower = raw.toLowerCase();
      if (modifierAliases[lower]) return modifierAliases[lower];
      if (keyAliases[lower]) return keyAliases[lower];
      if (raw.length === 1) return raw.toUpperCase();
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    })
    .join("+");
}

export function formatShortcut(
  hotkey: string | string[],
  platform: ShortcutPlatform = detectShortcutPlatform()
): string {
  return formatForDisplay(normalizeShortcutKeys(hotkey), { platform });
}
